"use client"

import { useCallback, useEffect, useRef, useState } from "react"
import { tosStatusAPI, tosAcceptAPI } from "@/app/apiPaths"
import { GetTosStatusResponse } from "@/pb/api/devplan/services/web/tos"
import { useUser } from "@/hooks/queries/use-users"
import logger from "@/app/logger"

// Session storage helpers for TOS acceptance caching (version-keyed)
function readIsTosAcceptanceTrueInCache(versionId: number): boolean {
  if (typeof window === "undefined") return false
  try {
    return sessionStorage.getItem(`tos_accepted_v:${versionId}`) === "true"
  } catch {
    return false
  }
}

function storeTosAcceptanceTrueInCache(versionId: number): void {
  if (typeof window === "undefined") return
  try {
    sessionStorage.setItem(`tos_accepted_v:${versionId}`, "true")
  } catch {
    // Ignore sessionStorage errors (e.g., quota exceeded)
  }
}

export interface UseTosAcceptanceFlowResult {
  shouldShowModal: boolean
  currentVersionId: number | null
  submitting: boolean
  error: string | null
  acceptFn: () => Promise<void>
}

/**
 * Consolidated hook for TOS acceptance flow.
 *
 * Handles:
 * - Fetching TOS status from backend
 * - Session caching of acceptance
 * - Exposing state and accept action for UI
 *
 * The flow is:
 * 1. Fetch TOS status from backend
 * 2. Check sessionStorage cache
 * 3. If already accepted, cache and done
 * 4. If not accepted, show modal
 */
export function useTosAcceptanceFlow(): UseTosAcceptanceFlowResult {
  const { user } = useUser()

  const [shouldShowModal, setShouldShowModal] = useState(false)
  const [currentVersionId, setCurrentVersionId] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Ref to prevent double-execution in React Strict Mode
  const flowCheckedRef = useRef(false)

  // Main effect: check TOS status on user load
  useEffect(() => {
    if (flowCheckedRef.current) return
    if (!user) return

    flowCheckedRef.current = true

    // By default, shouldShowModal is false, and we only set it to true when we
    // confirmed for sure that the user has not accepted ToS yet - at the end of the flow

    const runFlow = async () => {
      logger.debug({ action: "tos_flow_start" }, "Starting TOS acceptance flow")

      try {
        // Step 1: Fetch TOS status from backend
        const statusResponse = await fetch(tosStatusAPI(), {
          credentials: "same-origin",
        })

        if (!statusResponse.ok) {
          logger.error(
            { action: "tos_status_check_failed", status: statusResponse.status },
            "Failed to check TOS status"
          )
          return
        }

        const statusData = GetTosStatusResponse.fromJSON(await statusResponse.json())
        const versionId = statusData.currentTosVersionId
        setCurrentVersionId(versionId)

        logger.debug(
          { hasAccepted: statusData.hasAccepted, tosVersionId: versionId },
          "TOS status check result"
        )

        // Step 2: Check sessionStorage cache
        if (readIsTosAcceptanceTrueInCache(versionId)) {
          logger.debug(
            { action: "tos_cache_hit", tosVersionId: versionId },
            "TOS acceptance cached in session"
          )
          return
        }

        // Step 3: If already accepted according to backend, cache and done
        if (statusData.hasAccepted) {
          storeTosAcceptanceTrueInCache(versionId)
          return
        }

        // Step 4: User has not accepted - show modal
        logger.info(
          { action: "tos_modal_shown", tosVersionId: versionId },
          "Showing TOS acceptance modal"
        )
        setShouldShowModal(true)
      } catch (err) {
        logger.error({ action: "tos_flow_error", error: err }, "Error in TOS acceptance flow")
      }
    }

    runFlow()
  }, [user])

  // Accept handler for modal submission
  const acceptHandler = useCallback(async () => {
    if (submitting) return

    setSubmitting(true)
    setError(null)

    logger.debug({ action: "tos_accept_submit" }, "Submitting TOS acceptance from modal")

    try {
      const response = await fetch(tosAcceptAPI(), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({ acceptMethod: "dialog" }),
      })

      if (response.ok) {
        logger.info(
          { action: "tos_accept_success", tosVersionId: currentVersionId },
          "TOS acceptance recorded successfully"
        )
        if (currentVersionId !== null) {
          storeTosAcceptanceTrueInCache(currentVersionId)
        }
        setShouldShowModal(false)
      } else {
        const errorText = await response.text().catch(() => "Unknown error")
        logger.error(
          { action: "tos_accept_failed", status: response.status, error: errorText },
          "Failed to submit TOS acceptance"
        )
        setError("Failed to record acceptance. Please try again.")
      }
    } catch (err) {
      logger.error({ action: "tos_accept_error", error: err }, "Error submitting TOS acceptance")
      setError("Failed to record acceptance. Please try again.")
    } finally {
      setSubmitting(false)
    }
  }, [submitting, currentVersionId])

  return {
    shouldShowModal,
    currentVersionId,
    submitting,
    error,
    acceptFn: acceptHandler,
  }
}
